// src/pages/company/TechnicianSearch.js
import React, { useState, useEffect } from 'react';
import { 
  FiSearch, FiFilter, FiUser, FiMapPin, FiBriefcase, 
  FiMail, FiStar, FiMessageSquare, FiX, FiChevronDown, FiDownload, FiSend 
} from 'react-icons/fi';
import supabase from '../../supabase';
import ProfileCard from '../../components/shared/ProfileCard'; 

import { useNavigate } from 'react-router-dom';
import { messageService } from '../../services/messageService';


const calculateAge = (birthdate) => {
  const today = new Date();
  const birthDateObj = new Date(birthdate);
  let age = today.getFullYear() - birthDateObj.getFullYear();
  const m = today.getMonth() - birthDateObj.getMonth();
  
  if (m < 0 || (m === 0 && today.getDate() < birthDateObj.getDate())) {
    age--;
  }
  
  return age;
};

const TechnicianSearch = () => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedTechnicianForMessage, setSelectedTechnicianForMessage] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const navigate = useNavigate(); // Para redirigir al centro de mensajes

  const availableSkills = [
    'Audio', 'Iluminación', 'Video', 'Producción', 'Escenografía',
    'Multimedia', 'Streaming', 'LED', 'Consolas Digitales', 'DJ'
  ];

  // Función para contactar a un técnico desde la tarjeta
  const handleContactTechnician = async (technicianId, e) => {
    e.stopPropagation(); // Prevenir que se abra el modal al hacer clic en "Contactar"
    try {
      navigate('/member/messages', { state: { openConversationWith: technicianId } });
  } catch (error) {
      console.error('Error al iniciar conversación:', error);
    }
  };

  // Función para contactar desde el modal
  const handleContactFromModal = async (technicianId) => {
    try {
      closeModal(); // Cerrar el modal antes de redirigir
      navigate('/company/messages', { 
        state: { openConversationWith: technicianId } 
      });
    } catch (error) {
      console.error('Error al iniciar conversación:', error);
    }
  };

  // Función para abrir el modal de mensaje
  const handleOpenMessageModal = (technician, e) => {
    e.stopPropagation();
    setSelectedTechnicianForMessage(technician);
    setShowMessageModal(true);
  };

  // Función para enviar el mensaje
  const handleSendMessage = async () => {
    if (!messageContent.trim()) return;

    try {
      setSendingMessage(true);
      await messageService.sendMessage(
        selectedTechnicianForMessage.id,
        `Contacto inicial - ${selectedTechnicianForMessage.first_name} ${selectedTechnicianForMessage.last_name}`,
        messageContent
      );

      // Navegar al centro de mensajes
      navigate('/company/messages', {
        state: {
          openConversationWith: selectedTechnicianForMessage.id,
        },
      });
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
    } finally {
      setSendingMessage(false);
      setShowMessageModal(false);
      setMessageContent('');
    }
  };

  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        setLoading(true);
        
        // Consulta a la base de datos real
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('is_admin', false)
          .eq('is_trainer', false)
          .not('first_name', 'is', null)
          .not('last_name', 'is', null);
          
        if (error) throw error;
        
        // Procesar técnicos
        const processedTechnicians = data.map(tech => ({
          id: tech.id,
          first_name: tech.first_name || '',
          last_name: tech.last_name || '',
          profile_image_url: tech.profile_image,
          profile_image_path: tech.profile_image_path,
          job_title: tech.job_title || 'Técnico Audiovisual',
          city: tech.city || '',
          country: tech.country || '',
          skills: getSkillsFromProfile(tech),
          bio: tech.bio || 'Sin biografía disponible',
          has_cv: tech.has_cv_enabled || false,
          membership_level: tech.membership_level || 'bronze',
          email: tech.email,
          birthdate: tech.birthdate || null,
          age: tech.birthdate ? calculateAge(tech.birthdate) : null 
        }));
        
        // Extraer países y ciudades únicas
        const countries = [...new Set(processedTechnicians
          .map(tech => tech.country)
          .filter(country => country && country.trim() !== ''))];
        
        setAvailableCountries(countries);
        setTechnicians(processedTechnicians);
        
        // Mezclar aleatoriamente los resultados para la vista inicial
        shuffleArray(processedTechnicians);
        
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar técnicos:', error);
        setLoading(false);
      }
    };

    fetchTechnicians();
  }, []);
  
  // Actualizar ciudades disponibles cuando cambia el país seleccionado
  useEffect(() => {
    if (selectedCountry) {
      const citiesInCountry = [...new Set(technicians
        .filter(tech => tech.country === selectedCountry)
        .map(tech => tech.city)
        .filter(city => city && city.trim() !== ''))];
      
      setAvailableCities(citiesInCountry);
    } else {
      setAvailableCities([]);
    }
    
    // Si cambió el país, resetear la ciudad seleccionada
    setSelectedCity('');
  }, [selectedCountry, technicians]);
  
  // Función para extraer habilidades del perfil
  const getSkillsFromProfile = (profile) => {
    const skills = [];
    
    if (profile.interest_audio) skills.push('Audio');
    if (profile.interest_video) skills.push('Video');
    if (profile.interest_lighting) skills.push('Iluminación');
    if (profile.interest_mixing) skills.push('Mezcla');
    if (profile.interest_recording) skills.push('Grabación');
    if (profile.interest_streaming) skills.push('Streaming');
    if (profile.interest_liveevents) skills.push('Eventos en vivo');
    if (profile.interest_production) skills.push('Producción');
    if (profile.interest_installation) skills.push('Instalación');
    
    // Si no tiene habilidades específicas, asignar algunas por defecto
    if (skills.length === 0) {
      return ['Técnico Audiovisual'];
    }
    
    return skills;
  };
  
  // Función para mezclar aleatoriamente un array
  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  // Filtrar técnicos basado en búsqueda y filtros
  const filteredTechnicians = technicians.filter(technician => {
    // Filtro por término de búsqueda
    const searchMatch = searchTerm === '' || 
      `${technician.first_name} ${technician.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      technician.job_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (technician.bio && technician.bio.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Filtro por habilidades
    const skillsMatch = selectedSkills.length === 0 || 
      selectedSkills.some(skill => technician.skills.includes(skill));
    
    // Filtro por país
    const countryMatch = selectedCountry === '' || technician.country === selectedCountry;
    
    // Filtro por ciudad
    const cityMatch = selectedCity === '' || technician.city === selectedCity;
    
    return searchMatch && skillsMatch && countryMatch && cityMatch;
  });

  const toggleSkill = (skill) => {
    if (selectedSkills.includes(skill)) {
      setSelectedSkills(selectedSkills.filter(s => s !== skill));
    } else {
      setSelectedSkills([...selectedSkills, skill]);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCountryChange = (e) => {
    setSelectedCountry(e.target.value);
  };
  
  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
  };
  
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSkills([]);
    setSelectedCountry('');
    setSelectedCity('');
  };
  
  const openTechnicianModal = (technician) => {
    setSelectedTechnician(technician);
    setModalOpen(true);
  };
  
  const closeModal = () => {
    setModalOpen(false);
    setSelectedTechnician(null);
  };
  
  // Función para construir la URL de la imagen de perfil
  const getProfileImageUrl = (technician) => {
    if (technician.profile_image_url) return technician.profile_image_url;
    
    if (technician.profile_image_path) {
      return `https://izghgwyancrwsuxajfah.supabase.co/storage/v1/object/public/profilepictures/${technician.profile_image_path}`;
    }
    
    return null; // Sin imagen de perfil
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Búsqueda de Profesionales2</h1>
      </div>
      
      {/* Barra de búsqueda */}
      <div className="bg-dark-800 rounded-lg border border-dark-700 p-4">
        <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-3">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Buscar por nombre, especialidad o palabra clave..."
              className="w-full bg-dark-700 border border-dark-600 rounded-lg py-2 pl-10 pr-4 text-white focus:outline-none focus:border-primary-500"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="py-2 px-4 bg-dark-700 hover:bg-dark-600 text-white rounded-lg flex items-center justify-center transition-colors border border-dark-600"
          >
            <FiFilter className="mr-2" />
            Filtros {(selectedSkills.length > 0 || selectedCountry || selectedCity) && 
                    `(${selectedSkills.length + (selectedCountry ? 1 : 0) + (selectedCity ? 1 : 0)})`}
          </button>
        </div>
        
        {/* Panel de filtros */}
        {showFilters && (
          <div className="mt-4 p-4 bg-dark-700 rounded-lg border border-dark-600">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-gray-400 text-sm mb-2">Habilidades</h3>
                <div className="flex flex-wrap gap-2">
                  {availableSkills.map(skill => (
                    <button
                      key={skill}
                      onClick={() => toggleSkill(skill)}
                      className={`px-3 py-1 rounded-full text-xs transition-colors ${
                        selectedSkills.includes(skill)
                          ? 'bg-primary-600 text-white'
                          : 'bg-dark-600 text-gray-300 hover:bg-dark-500'
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-gray-400 text-sm mb-2">Ubicación</h3>
                <div className="space-y-2">
                  {/* Selector de país */}
                  <div className="relative">
                    <select
                      value={selectedCountry}
                      onChange={handleCountryChange}
                      className="w-full bg-dark-600 border border-dark-500 rounded-lg py-2 pl-3 pr-10 text-white focus:outline-none focus:border-primary-500 appearance-none"
                    >
                      <option value="">Todos los países</option>
                      {availableCountries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                    <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                  </div>
                  
                  {/* Selector de ciudad (visible solo si hay país seleccionado) */}
                  {selectedCountry && availableCities.length > 0 && (
                    <div className="relative">
                      <select
                        value={selectedCity}
                        onChange={handleCityChange}
                        className="w-full bg-dark-600 border border-dark-500 rounded-lg py-2 pl-3 pr-10 text-white focus:outline-none focus:border-primary-500 appearance-none"
                      >
                        <option value="">Todas las ciudades</option>
                        {availableCities.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                      <FiChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-gray-400 hover:text-white text-sm"
              >
                Limpiar filtros
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Resultados */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium">
            {filteredTechnicians.length} técnicos encontrados
          </h2>
        </div>
        
        {filteredTechnicians.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTechnicians.map(technician => (
  <ProfileCard
    key={technician.id}
    profile={technician}
    variant="grid"
    showActions={true}
    actions={{
      onContact: (tech) => handleOpenMessageModal(tech, { stopPropagation: () => {} }),
      onFollow: true
    }}
    className="h-full"
  />
))}
          </div>
        ) : (
          <div className="bg-dark-800 rounded-lg border border-dark-700 p-8 text-center">
            <p className="text-gray-400 mb-4">No se encontraron técnicos con los criterios seleccionados.</p>
            <button
              onClick={clearFilters}
              className="bg-primary-600 hover:bg-primary-500 text-white py-2 px-4 rounded-lg inline-flex items-center transition-colors"
            >
              Limpiar filtros
            </button>
          </div>
        )}
      </div>
      
      {/* Modal de detalle */}
      {modalOpen && selectedTechnician && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 px-4">
          <div className="bg-dark-800 rounded-lg border border-dark-700 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-5">
              {/* Header con botón de cerrar */}
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Perfil del Técnico</h2>
                <button 
                  onClick={closeModal}
                  className="text-gray-400 hover:text-white"
                >
                  <FiX size={24} />
                </button>
              </div>
              
              {/* Información principal */}
              <div className="flex flex-col md:flex-row gap-6">
                {/* Imagen de perfil */}
                <div className="w-full md:w-1/3 flex justify-center">
                  <div className="w-32 h-32 md:w-full md:h-64 max-w-md rounded-lg bg-dark-700 flex items-center justify-center overflow-hidden">
                    {getProfileImageUrl(selectedTechnician) ? (
                      <img 
                        src={getProfileImageUrl(selectedTechnician)} 
                        alt={`${selectedTechnician.first_name} ${selectedTechnician.last_name}`} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-4xl font-semibold text-gray-300">
                        {selectedTechnician.first_name[0]}{selectedTechnician.last_name[0]}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Datos personales */}
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h1 className="text-2xl font-bold text-white">
                        {selectedTechnician.first_name} {selectedTechnician.last_name}
                      </h1>
                      {selectedTechnician.age !== null && (
                        <p className="text-gray-400 text-sm mt-1">
                          {selectedTechnician.age} años
                        </p>
                      )}
                      <p className="text-primary-400 text-lg">{selectedTechnician.job_title}</p>
                    </div>
                    
                    {selectedTechnician.membership_level === 'gold' && (
                      <span className="bg-yellow-500 text-dark-900 px-3 py-1 rounded-full font-medium text-sm">
                        Gold Member
                      </span>
                    )}
                  </div>
                  
                  {/* Ubicación */}
                  {(selectedTechnician.city || selectedTechnician.country) && (
                    <div className="flex items-center mt-3 text-gray-300">
                      <FiMapPin className="mr-2" />
                      <span>
                        {selectedTechnician.city && selectedTechnician.country 
                          ? `${selectedTechnician.city}, ${selectedTechnician.country}`
                          : selectedTechnician.city || selectedTechnician.country}
                      </span>
                    </div>
                  )}
                  
                  {/* Email */}
                  {selectedTechnician.email && (
                    <div className="flex items-center mt-2 text-gray-300">
                      <FiMail className="mr-2" />
                      <span>{selectedTechnician.email}</span>
                    </div>
                  )}
                  
                  {/* Habilidades */}
                  <div className="mt-4">
                    <h3 className="text-lg font-medium mb-2">Habilidades</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedTechnician.skills.map(skill => (
                        <span key={skill} className="px-3 py-1 bg-dark-700 text-gray-300 rounded-full text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Biografía */}
                  <div className="mt-4">
                    <h3 className="text-lg font-medium mb-2">Biografía</h3>
                    <p className="text-gray-300">
                      {selectedTechnician.bio || 'El técnico no ha agregado una biografía aún.'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* CV si está disponible */}
              {selectedTechnician.has_cv && (
                <div className="mt-6 p-4 border border-dark-600 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium">Currículum Vitae</h3>
                      <p className="text-gray-400 text-sm">El técnico ha habilitado su CV completo.</p>
                    </div>
                    <button 
                      className="flex items-center bg-primary-600 hover:bg-primary-500 text-white py-2 px-4 rounded-lg text-sm transition-colors"
                      onClick={() => handleContactFromModal(selectedTechnician.id)}
                    >
                      <FiDownload className="mr-2" />
                      Descargar CV
                    </button>
                  </div>
                </div>
              )}
              
              {/* Acciones */}
              <div className="mt-6 border-t border-dark-700 pt-6 flex flex-wrap gap-3">
                <button 
                  className="flex-1 min-w-32 py-3 px-4 bg-primary-600 hover:bg-primary-500 text-white rounded-lg flex items-center justify-center text-sm transition-colors"
                  onClick={() => handleContactFromModal(selectedTechnician.id)}
                >
                  <FiMessageSquare className="mr-2" size={16} />
                  Enviar mensaje
                </button>
               

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de mensaje */}
      {showMessageModal && selectedTechnicianForMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 px-4">
          <div className="bg-dark-800 rounded-lg border border-dark-700 w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">
                Enviar mensaje a {selectedTechnicianForMessage.first_name}{' '}
                {selectedTechnicianForMessage.last_name}
              </h3>
              <button
                onClick={() => setShowMessageModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <FiX size={24} />
              </button>
            </div>

            <textarea
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-3 text-white focus:outline-none focus:border-primary-500 mb-4"
              rows={4}
              placeholder="Escribe tu mensaje..."
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
            />

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowMessageModal(false)}
                className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!messageContent.trim() || sendingMessage}
                className={`px-4 py-2 rounded-lg flex items-center ${
                  !messageContent.trim() || sendingMessage
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-500'
                } text-white`}
              >
                {sendingMessage ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <FiSend className="mr-2" />
                    Enviar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicianSearch;